import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {getActiveSubjectMessage, getMessageTemplate} from '../utils';
import {NewEmailTemplate} from '../parts/NewEmailTemplate/NewEmailTemplate';
import {messageLangs} from '../constants';
import {languageTabs} from '../../../AutomationActionToolbar/constants';
import {GetMessageColumns} from '../../../../../../../EmailEditor/services';
import {serializedTreeResults} from '../../../../../../../EmailEditor/utils/serializedTreeResults';
import {TreeNodeProps} from '@priority-software/priority-style-react';
import {formsStore} from '../../../../../../../../stores/Form/FormsStore';
import {showFormClientSideMessage} from '../../../../../../../Form/actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';
import {texts} from '../../../../../../../EmailEditor/texts';
import {getFormPathArray} from '../../../../../../../EmailEditor/utils/getFormPathArray';
import {isCurrentEntBPM} from '../../../../../../../../_shared/GWTBridge/invoke/genPage';
import {getBpmBaseFormName} from '../../../../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';

export const useNewTemplate = ({
    state,
    newEmailTemplate,
    openRule,
    supportEmailEditor,
    changeCoverLetter
}) => {
    const [isOpenEmailDialog, setIsOpenEmailDialog] = useState(false);
    const [treeValues, setTreeValues] = useState<TreeNodeProps>(null);

    const isNewTemplate = useMemo(() => getMessageTemplate(state), []);

    const messageValueDisplay = getActiveSubjectMessage(state);

    const isNewEmailAllow = useMemo(() => {
        const searchParams = new URLSearchParams(window.location.search);

        return searchParams.get('_newemail') === '1';
    }, []);

    const isDisabledOldTemplate = useMemo(
        () =>
            isNewEmailAllow &&
            supportEmailEditor &&
            openRule?.AlertNumber?.toString() &&
            isNewTemplate,
        [supportEmailEditor, openRule?.AlertNumber?.toString(), isNewTemplate]
    );

    const messagesValue = useMemo(() => {
        if (!newEmailTemplate) {
            if (Object.keys(state.CoverLetters)?.length > 0) {
                return state.CoverLetters;
            } else return state.Messages;
        }

        return languageTabs.reduce((acc, lang) => {
            const language = messageLangs?.[lang.id];
            const Subject = newEmailTemplate?.[lang.id]?.editorSubject;
            const Message = newEmailTemplate?.[lang.id]?.emailHtml;

            return {
                ...acc,
                [language]: {
                    ...state.Messages[language],
                    Subject,
                    Message
                }
            };
        }, {});
    }, [state.Messages, newEmailTemplate]);

    useEffect(() => {
        messagesValue && changeCoverLetter(messagesValue);
    }, [messagesValue]);

    const handleEmailDialog = useCallback(async () => {
        const onConfirm = async () => {
            if (!isOpenEmailDialog) {
                const formPathArray = isCurrentEntBPM()
                    ? [getBpmBaseFormName()]
                    : getFormPathArray(formsStore.getActiveForm());
                const treeBranchesRes = await GetMessageColumns(formPathArray);
                setTreeValues(serializedTreeResults(treeBranchesRes?.ColumnsTree, true));
            }

            setIsOpenEmailDialog(prev => !prev);
        };

        if (isDisabledOldTemplate && !isOpenEmailDialog) {
            return new Promise(resolve => {
                showFormClientSideMessage('', priority.netitems.shared.Message.Type.Warning, {
                    title: texts.edit_new_email_message_title,
                    content: texts.edit_new_email_message_content,
                    confirmButtonText: texts.edit_new_email_message_button,
                    cancleButtonText: texts.edit_new_email_message_cancel,
                    onConfirm: () => resolve(onConfirm()),
                    onClose: () => resolve(null)
                });
            });
        }

        await onConfirm();
        return null;
    }, [isOpenEmailDialog]);

    const renderNewTemplate = useCallback(
        renderComponent => (
            <>
                <NewEmailTemplate {...{handleEmailDialog, subject: messageValueDisplay?.Subject}} />
                {renderComponent()}
            </>
        ),
        [messageValueDisplay?.Subject]
    );

    return {
        isNewTemplate,
        messageValueDisplay,
        isOpenEmailDialog,
        handleEmailDialog,
        renderNewTemplate,
        messagesValue,
        treeValues,
        isNewEmailAllow,
        isDisabledOldTemplate
    } as const;
};
